import { FormControl, FormField } from '@/components/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Form, FormInput, FormItem, FormLabel } from '@/components/wm';
import { zodResolver } from '@hookform/resolvers/zod';
import debounce from 'lodash/debounce';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { paletteVariants } from '../Widgets/ValueCardWidget';
import { Widget } from './Widget';

export function NewValueWidgetGeneral ({ data, onChangeField, onCompleted }) {
    const paletteOptions = Object.keys(paletteVariants);

    const formSchema = z.object({
        legend: z.string().optional(),
        palette: z.enum(paletteOptions, {
            errorMap: () => ({ message: i18next.t('form_validations.widget.palette_required') })
        }),
        subtitle: z.string().optional(),
        title: z.string().min(1, i18next.t('form_validations.generic.required'))
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            legend: data.legend || '',
            palette: data.graphical?.palette || 'primary',
            subtitle: data.subtitle || '',
            title: data.title || ''
        },
        mode: 'onBlur'
    });

    const debouncedTriggers = useRef({
        palette: debounce(() => form.trigger('palette'), 600),
        title: debounce(() => form.trigger('title'), 600)
    });

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid]);

    useEffect(() => {
        return () => {
            Object.values(debouncedTriggers.current).forEach(fn => fn.cancel());
        };
    }, []);

    return (
        <div className="tw-flex tw-gap-6">
            <div className="tw-flex-1">
                <Form {...form}>
                    <FormField
                        control={form.control}
                        name="title"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('glossary.title').capitalize()}</FormLabel>
                                <FormControl>
                                    <FormInput
                                        {...field}
                                        onChange={e => {
                                            field.onChange(e);
                                            onChangeField('title')(e);
                                        }}
                                        placeholder={i18next.t('glossary.title').capitalize()}
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="subtitle"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('glossary.subtitle').capitalize()}</FormLabel>
                                <FormControl>
                                    <FormInput
                                        {...field}
                                        onChange={e => {
                                            field.onChange(e);
                                            onChangeField('subtitle')(e);
                                        }}
                                        placeholder={i18next.t('glossary.subtitle').capitalize()}
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="legend"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('common.legend').capitalize()}</FormLabel>
                                <FormControl>
                                    <FormInput
                                        {...field}
                                        onChange={e => {
                                            field.onChange(e);
                                            onChangeField('legend')(e);
                                        }}
                                        placeholder={i18next.t('common.legend').capitalize()}
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="palette"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('glossary.palette').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(val);
                                            // Save to nested graphical.palette structure
                                            onChangeField('graphical')({
                                                ...data.graphical,
                                                palette: val
                                            });
                                            debouncedTriggers.current.palette();
                                        }}
                                        value={field.value}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('dashboard.custom_dashboard.select_a_palette')}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {paletteOptions.map(palette => (
                                                <SelectItem key={palette} value={palette}>
                                                    {palette.capitalize()}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                </Form>
            </div>
            <div className="tw-w-[320px] tw-h-[200px] tw-flex-shrink-0">
                <Widget widget={data}/>
            </div>
        </div>
    );
}
